import { z } from 'zod';

/**
 * Provisionssätze wie mp_rate_for der PHP-Middleware. Es gilt der erste Treffer in dieser Reihenfolge.
 * Abweichung: Ein Händlersatz von 0 % gilt hier (leer = nicht gesetzt); PHP kannte nur „> 0“.
 */
export const PROVISION_QUELLEN = {
  produkt: 'Produkt',
  haendler_kategorie: 'Kategorie beim Händler',
  kategorie: 'Kategorie',
  haendler: 'Händler',
  standard: 'Standard',
} as const;
export type ProvisionQuelle = keyof typeof PROVISION_QUELLEN;
export const PROVISION_REIHENFOLGE = Object.keys(PROVISION_QUELLEN) as ProvisionQuelle[];

/** Prozent vom Verkaufspreis, 0–100 mit höchstens zwei Nachkommastellen */
export const ProvisionsSatz = z
  .number({ invalid_type_error: 'Bitte eine Zahl eingeben' })
  .min(0, 'Mindestens 0 %')
  .max(100, 'Höchstens 100 %')
  .multipleOf(0.01, 'Höchstens zwei Nachkommastellen');

const Kategorie = z.string().trim().min(1, 'Bitte eine Kategorie angeben').max(120);

export const StandardSatzSchema = z.object({ satz: ProvisionsSatz });
export type StandardSatz = z.infer<typeof StandardSatzSchema>;

/** satz = null entfernt den Kategoriesatz */
export const KategorieSatzSchema = z.object({ kategorie: Kategorie, satz: ProvisionsSatz.nullable() });
export type KategorieSatz = z.infer<typeof KategorieSatzSchema>;

/** Grundsatz des Händlers (haendler.provision) und seine Sondersätze je Kategorie – ersetzt alle bisherigen */
export const HaendlerSaetzeSchema = z.object({
  provision: ProvisionsSatz.nullable(),
  kategorien: z.record(Kategorie, ProvisionsSatz).refine((k) => Object.keys(k).length <= 500, 'Höchstens 500 Sondersätze'),
});
export type HaendlerSaetze = z.infer<typeof HaendlerSaetzeSchema>;

/** satz = null: Produkt folgt wieder den Regeln */
export const ProduktSatzSchema = z.object({ satz: ProvisionsSatz.nullable() });
export type ProduktSatz = z.infer<typeof ProduktSatzSchema>;

export const ProvisionVorschauAbfrage = z
  .object({
    produkt: z.string().uuid().optional(),
    haendler: z.string().uuid().optional(),
    kategorie: z.string().trim().max(120).optional(),
  })
  .refine((a) => a.produkt || a.haendler || a.kategorie, 'Bitte ein Produkt, einen Händler oder eine Kategorie wählen');

export const ProvisionHaendlerAbfrage = z.object({
  q: z.string().trim().max(100).optional(),
  /** nur Händler mit eigenem Satz oder Sondersätzen */
  eigene: z.enum(['1', '0']).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

export const ProvisionAusnahmenAbfrage = z.object({
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

/** Wie viele Produkte jede Stufe entscheidet (ohne entfernte Produkte) */
export type ProvisionAuswirkung = Record<ProvisionQuelle, number>;

export interface ProvisionKategorie {
  /** '' = Produkte ohne Kategorie (dort greifen keine Kategoriesätze) */
  kategorie: string;
  satz: number | null;
  produkte: number;
  auswirkung: ProvisionAuswirkung;
}

export interface ProvisionUebersicht {
  standard: number;
  kategorien: ProvisionKategorie[];
  /** Kategorien gekürzt (sehr viele Kategorien) */
  kategorienGekuerzt: boolean;
  produkte: number;
  auswirkung: ProvisionAuswirkung;
  haendlerMitSatz: number;
  sondersaetze: number;
}

export interface ProvisionHaendlerZeile {
  id: string;
  firma: string;
  code: string | null;
  status: string;
  provision: number | null;
  sondersaetze: number;
  produkte: number;
}

export interface ProvisionHaendlerSeite {
  eintraege: ProvisionHaendlerZeile[];
  weiter: string | null;
}

export interface ProvisionHaendlerDetail {
  id: string;
  firma: string;
  code: string | null;
  provision: number | null;
  standard: number;
  /** Kategorien des Händlers (mit Produkten) und solche mit Sondersatz */
  kategorien: { kategorie: string; produkte: number; sondersatz: number | null; kategoriesatz: number | null }[];
}

export interface ProvisionAusnahme {
  id: string;
  titel: string;
  haendlerFirma: string;
  kategorie: string;
  satz: number;
}

export interface ProvisionAusnahmenSeite {
  eintraege: ProvisionAusnahme[];
  weiter: string | null;
  gesamt: number;
}

export interface ProvisionStufe {
  quelle: ProvisionQuelle;
  satz: number | null;
}

export interface ProvisionVorschau {
  satz: number;
  quelle: ProvisionQuelle;
  /** alle fünf Stufen in Reihenfolge – die erste mit Satz gilt */
  stufen: ProvisionStufe[];
  produkt: { id: string; titel: string; preis: number | null } | null;
  haendler: { id: string; firma: string } | null;
  kategorie: string;
}

/** 19.5 → „19,5 %“ */
export function provisionText(satz: number): string {
  return `${satz.toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;
}
